import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../../components/common/Logo';
import { authService } from '../../services';
import { validateEmail } from '../../utils/validation';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const initialEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('email') || '').trim().toLowerCase();
  }, [location.search]);

  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const successMessage =
    t('auth.forgotPassword.successMessage') ||
    'If an account exists for this email, a password reset link has been sent.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const v = validateEmail(email);
    setFieldError(v);
    if (v) return;

    setLoading(true);
    try {
      await authService.forgotPassword({ email: email.trim().toLowerCase() });
      // Always show success UI (do not leak whether email exists)
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      const hasResponse = !!err?.response;

      // Only show error on network issues or 5xx
      if (!hasResponse || (typeof status === 'number' && status >= 500)) {
        setError(t('common.error') || 'Error');
      } else {
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.forgotPassword.title')}</h2>
          <p className="mt-2 text-gray-600">{t('auth.forgotPassword.subtitle')}</p>
        </div>

        {submitted && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.email')} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldError) setFieldError('');
              }}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              placeholder="yourname@example.com"
              required
            />
            {fieldError && <p className="mt-1 text-sm text-red-600">{fieldError}</p>}
          </div>

          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('common.loading') : t('auth.forgotPassword.sendLink')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-primary-600 font-semibold hover:text-primary-700">
            ← {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

